import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import appEnv from '@/support/env'
import { useTopProgress } from '@/shared/lib'

import {
  ensureSession,
  impersonate,
  readStaffBadge
} from '@/apps/adok/entities/account'
import type { ImpersonationTicket } from '@/apps/adok/entities/account'

import { AdokLayout as Layout } from './layout'

const DashboardPage = () =>
  import('@/apps/adok/pages/dashboard').then((m) => m.DashboardPage)

const NotFoundPage = () =>
  import('@/apps/adok/pages/not-found').then((m) => m.NotFoundPage)

import authRoutes from '@/apps/adok/routes/auth'
import legacyRedirects from '@/apps/adok/routes/legacy-redirects'
import unsubscribeRoutes from '@/apps/adok/routes/unsubscribe'
import trafficSourcesRoutes from '@/apps/adok/routes/traffic-sources'
import websitesRoutes from '@/apps/adok/routes/websites'
import antiAdblockDomainsRoutes from '@/apps/adok/routes/anti-adblock-domains'
import referralsRoutes from '@/apps/adok/routes/referrals'
import malwareRoutes from '@/apps/adok/routes/malware'
import mediaKitsRoutes from '@/apps/adok/routes/media-kits'
import flatDealsRoutes from '@/apps/adok/routes/flat-deals'
import marketplaceRoutes from '@/apps/adok/routes/marketplace'
import rotationRoutes from '@/apps/adok/routes/rotation'
import spotsRoutes from '@/apps/adok/routes/spots'
import reportsRoutes from '@/apps/adok/routes/reports'
import offerRestrictionsRoutes from '@/apps/adok/routes/offer-restrictions'
import balanceRoutes from '@/apps/adok/routes/balance'
import profileRoutes from '@/apps/adok/routes/profile'
import masterSpotCheckRoutes from '@/apps/adok/routes/master-spot-check'

// Where a signed-out visitor was going; the login form reloads to `/` and loses the route.
const PENDING_ROUTE_KEY = 'adok:pending-route'

const routes: RouteRecordRaw[] = [
  {
    path: '/auth/:provider/callback',
    // Three declared params on purpose: vue-router auto-confirms a guard whose
    // `length < 3`, and this one deliberately never calls `next` — the popup is closing,
    // and resolving the navigation would render the blank component behind it first.
    beforeEnter(to, from, next) {
      window.close()
    },
    component: {
      template: '<div class="auth-component"></div>'
    }
  },
  ...authRoutes,
  ...unsubscribeRoutes,
  {
    path: '/404',
    component: NotFoundPage,
    meta: { hidden: true, title: '404 Not Found' }
  },
  {
    path: '/',
    component: Layout,
    children: [
      {
        // The dashboard is the app's root: there is no `/dashboard` path, and the logo in
        // the sidebar is what leads back here.
        path: '',
        name: 'dashboard',
        component: DashboardPage,
        meta: {
          title: 'Dashboard',
          root: true
        }
      }
    ]
  },
  ...reportsRoutes,
  ...profileRoutes,
  ...trafficSourcesRoutes,
  ...offerRestrictionsRoutes,
  ...balanceRoutes,
  ...websitesRoutes,
  ...antiAdblockDomainsRoutes,
  ...referralsRoutes,
  ...malwareRoutes,
  ...mediaKitsRoutes,
  ...flatDealsRoutes,
  ...marketplaceRoutes,
  ...rotationRoutes,
  ...spotsRoutes,
  ...masterSpotCheckRoutes,
  // Old paths adok no longer serves. Last on purpose: after every FSD array, so a live
  // route always wins the match, and before the catch-all, so an unknown path still 404s.
  ...legacyRedirects,
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404',
    meta: { hidden: true, title: '404 Not Found' }
  }
]
const router = createRouter({
  history: createWebHashHistory(appEnv.BASE_URL),
  routes,
  /**
   * Vue Router does nothing about scroll unless told to, so a list scrolled halfway down
   * opened the next page mid-scroll and Back never landed where the reader left.
   *
   * The section, not the path, decides. Every modal of a list is its own route rendering
   * the same page component under the same parent — `/traffic/spots`, `…/new`,
   * `…/:spotId/edit` — so keying on the path would scroll the list to the top every time a
   * modal opened over it. `matched[0]` is that shared parent, and comparing it means "a
   * different screen", which is the only case worth scrolling.
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition

    return to.matched[0]?.path === from.matched[0]?.path
      ? false
      : { top: 0, left: 0 }
  }
})
function savePendingRoute(fullPath: string) {
  if (fullPath === '/') return
  try {
    sessionStorage.setItem(PENDING_ROUTE_KEY, fullPath)
  } catch {
    // Storage blocked: nothing to remember, and nothing to report.
  }
}

function takePendingRoute(): string | null {
  try {
    const value = sessionStorage.getItem(PENDING_ROUTE_KEY)
    sessionStorage.removeItem(PENDING_ROUTE_KEY)
    return value
  } catch {
    return null
  }
}

/**
 * The URLs the backend lands a visitor on, none of which this router serves.
 *
 * A confirmation mail points at `/?confirmation_token=…`, a withdrawal mail at
 * `/withdrawal_requests/42/activate` — real server paths, while every route here lives in
 * the hash. Each one is answered by replacing the document with the hash URL that does
 * serve it.
 *
 * Read from `location.pathname` and `location.search`, and **neither changes while the app
 * runs**: hash routing rewrites only the fragment. So this can match on the first
 * navigation and never again, which is why `beforeEach` asks it exactly once instead of
 * rebuilding a `URL` and running two regexes on every route change.
 */
function landingRedirect(): string | null {
  const { pathname, search } = window.location
  const params = new URLSearchParams(search)

  const confirmationToken = params.get('confirmation_token')
  if (confirmationToken) return `/#/login/${confirmationToken}`

  const resetPasswordToken = params.get('reset_password_token')
  if (resetPasswordToken) return `/#/reset_password/${resetPasswordToken}`

  const token = params.get('token')
  if (token && pathname.includes('approve')) {
    return `/#/balance/withdrawals/approve?token=${encodeURIComponent(token)}`
  }

  const activating = pathname.match(/\/withdrawal_requests\/(\d+)\/activate/)
  if (activating) return `/#/balance/withdrawals/${activating[1]}/activate`

  if (/^\/withdrawal_requests\/?$/.test(pathname)) {
    return '/#/balance/withdrawals'
  }

  return null
}

/**
 * Staff impersonation: `/bcm/<key>?token=<token>` signs the session in as that account.
 * Landing-only for the same reason as `landingRedirect`, and checked after it — a URL
 * carrying both is a confirmation link first.
 */
function becomePayload(): ImpersonationTicket | null {
  const token = new URLSearchParams(window.location.search).get('token')
  const key = window.location.href.match(/\/bcm\/(.+)\?token/)?.[1]

  return token && key ? { token, key } : null
}

const DEFAULT_TITLE = 'Adok.ai - Your Personal Assistant for Ad Management'
let landingHandled = false

// Registered before the session guard, so the first load's session check shows the bar too.
const topProgress = useTopProgress()
router.beforeEach(() => {
  topProgress.start()
})
router.afterEach(() => {
  topProgress.done()
})
router.onError(() => {
  topProgress.done()
})

router.beforeEach(async (to, from, next) => {
  if (to.name === 'unsubscribe') {
    return next()
  }
  if (!landingHandled) {
    landingHandled = true

    const landing = landingRedirect()
    if (landing) {
      // No `next()` on purpose, here and below: the browser is leaving this document, and
      // resolving the navigation would first render a screen nobody gets to see.
      window.location.href = landing
      return
    }

    const ticket = becomePayload()
    if (ticket) {
      await impersonate(ticket)
      window.location.href = '/#/'
      return
    }
  }

  const loggedIn = await ensureSession()

  if (to.meta.auth && loggedIn) {
    next({ name: 'dashboard' })
  } else if (!to.meta.auth && loggedIn) {
    if (to.meta.staffOnly && !readStaffBadge().isStaff) {
      return next({ name: to.meta.staffOnly })
    }

    const pending = takePendingRoute()
    if (pending && pending !== to.fullPath) next(pending)
    else next()
  } else if (to.meta.auth && !loggedIn) {
    next()
  } else {
    savePendingRoute(to.fullPath)
    next({ name: 'login' })
  }
})

// The one place the tab title is written. Keep it that way: a second writer registered
// later wins, silently — see the note against `useLoading` in `src/adok.ts`.
router.afterEach((to, from) => {
  const { isStaff, accountId } = readStaffBadge()
  const staffPrefix = isStaff ? `[${accountId}] ` : ''
  document.title = to.meta.title
    ? `${staffPrefix}${to.meta.title} | ${DEFAULT_TITLE}`
    : `${staffPrefix}${DEFAULT_TITLE}`
})

export default router
